import { captureException, withScope } from '@sentry/browser'
import type { TFunction } from 'i18next'
import type { Dispatch, FC, ReactNode, SetStateAction } from 'react'
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'react-toastify'
import { match, P } from 'ts-pattern'
import { openMembershipMoreCreditsModal } from 'src/components/Membership/MembershipMoreCreditsModal'
import { openPromptsHelperEnabledHintDialog } from 'src/features/Prompt/components/promptsHelperEnabledHintDialog'
import { usePromptHelperMode } from 'src/features/Prompt/store/promptHelper'
import { mergePromptHelperParamsToParameter } from 'src/features/Prompt/utils/checkEnablePromptHelper'
import { useMyInfo } from 'src/services/apollo/auth'
import type { CreateGenerationTaskMutation } from 'src/services/apollo/generated/graphql'
import { useMyQuota } from 'src/services/apollo/quota'
import { executeCreateGenerationTask } from 'src/services/apollo/task'
import {
  getCreateTaskParameters,
  getCreateTaskTrackAttrs,
  isValuesCnetGenerativeFill,
  isValuesControlNet,
  isValuesLatentCouple,
} from 'src/services/helpers/artwork'
import { useDisableAutoPromptHelperPref } from 'src/services/helpers/preference'
import { getModelVersion } from 'src/stores/model'
import type { PromptsModerationAction } from 'src/utils/contentSafety'
import { PromptsModerationActions } from 'src/utils/contentSafety'
import {
  classifyErrorForTelemetry,
  extractPromptsModerationActionFromNsfwError,
  getErrorCode,
} from 'src/utils/errorUtil'
import { genId } from 'src/utils/genId'
import type { MaybeAsyncGetter } from 'src/utils/getter'
import { resolveGetter } from 'src/utils/getter'
import { useThrottledCallbackWithReturnValue } from 'src/utils/hooks/useThrottledCallbackWithReturnValue'
import type { QualityTag } from 'src/utils/model'
import { isChatModel, isLoraFeatureEnabled } from 'src/utils/model'
import {
  track,
  UpdateEventGroupAction,
  updateEventGroup,
} from 'src/utils/tracker'
import type { UnFile } from 'src/utils/unFile'
import type { HandleChange } from 'src/utils/useForm'
import { createHandleChange, useForm } from 'src/utils/useForm'
import useLoadingCallback from 'src/utils/useLoadingCallback'
import { openLoraStyleKeyWarnDialog } from '~/features/Generator/LoraWarnDialog/LoraWarnDialog'
import { checkLoraAndStyleKeyWarn } from '~/features/Generator/LoraWarnDialog/utils'
import { checkAgeRestriction } from '../Account/AgeRestriction'
import {
  openModelEarlyAccessNeedMembershipDialog,
  openModelPreviewTestIneligibleDialog,
} from '../GenerationModel/ModelPermissionDialog'
import { UIDialog } from '../ui/Modal'
import type { ValidateTaskParamsResult } from './Common/taskParamsValidator'
import { useValidateTaskParams } from './Common/taskParamsValidator'
import type { LoraParametersInfo } from './MultiColumnGenerationForm/Generation/RightCol/LoraSelector/loraParameters'
import type {
  I2VProFormArgs,
  I2vChannelType,
} from './MultiColumnGenerationForm/ImageToVideo/helper'
import { openSensitiveFeedbackToast } from './Sensitive/SensitiveFeedback'

export type TaskSubmitFormValues = Partial<{
  // task parameters
  prompts: string
  negativePrompts: string
  samplingSteps: number
  samplingMethod: string
  cfgScale: number
  seed: string
  priority: number
  /**
   * @deprecated Obsoleted preset model names
   */
  model: string
  modelId: string
  width: number // for i2i will be ignored by server, but still necessary for price calcing
  height: number

  /**
   * For i2i
   * @default 0.8 for variation
   * @default 0.55 for others
   */
  strength: number

  /**
   * For ControlNet
   */
  controlNets: ControlNetItem[]
  generativeFillType: 'task' | 'upload'
  fillEditorType: 'inpaint' | 'outpaint' | null

  batchSize: number // batch gen or not

  /**
   * LoRA, [loraModelId]: loraWeight
   */
  lora: Record<string, number>
  loraParameters: LoraParametersInfo[]

  /**
   * latent couple
   */
  latentCouple: {
    type: 'rect'
    divisions: string[]
    positions: string[]
    weights: number[]
  }

  /**
   * hires
   */
  upscaleDenoisingStrength: number // [0.01, 0.99] default: 0.6
  upscaleDenoisingSteps: number // [1, 50] default: scale
  upscaleSampler: string // same as samplingMethods
  /**
   * upscale
   */
  upscale: number
  /**
   * i2i upscale parameter
   */
  enableTile: boolean // default: false
  /**
   * enlarge
   */
  enlargeModel: string
  enlarge: number
  /**
   * A Detailer (Face Fix)
   * This feature is incompatible with Dit7B models
   */
  enableADetailer: boolean
  /**
   * {"minimum": 1, "maximum": 12, "step": 1, "default": 2}
   */
  clipSkip: number

  // input parameters
  baseImageTaskId: string
  baseImage: UnFile
  inpaintMaskImage: UnFile
  /** base + mask */
  previewImage: UnFile

  /**
   * Vae Model
   */
  vaeModelId?: string

  /**
   * A.K.A Magic Mode
   * This is a local state value
   */
  enablePromptsRewrite?: boolean

  extra: Partial<{
    // Natural Prompts for Magic Mode
    naturalPrompts?: string
  }>

  /**
   * IPAdapter
   */
  ipAdapter: {
    enabled?: boolean
    referenceImages?: string[]
    /** This flag has to be explicitly set to true to submit ipAdapter */
    imagesAreValid?: boolean
  }

  /**
   * Quality Tag
   */
  qualityTag: QualityTag
  /*
   * World & Character
   */
  characterId?: string

  styleMix: {
    enabled?: boolean
    seed?: number
    weight?: number
  }

  modelStyle?:
    | { type: 'key'; keyId: string }
    | { type: 'custom'; custom: string }
    | { type: 'styleKeyRef'; refId: string }

  lightning?: boolean

  watermark?: 'pixai'

  inferenceProfile?: string

  /**
   * Only available for specific models, e.g. hinata
   */
  inferenceConfig: Partial<{
    /** Explicitly set to null to disable this feature on supported models */
    rescaleCFG: number | null
    modelSampling: {
      inputs?: {
        shift?: number
      }
    }
  }>

  t2i2v: {
    enabled?: boolean
    i2vPro?: Pick<I2VProFormArgs, 'duration' | 'model' | 'mode'>
    channel?: I2vChannelType
  }

  multiImageReference: {
    referenceImages?: string[]
  }

  chatEditingConfig: {
    resolution?: string
    aspectRatio?: string
    quality?: string
  }

  /** Kaisuuken (benefit card) ID for free generation */
  kaisuukenId: string
}>

export type ControlNetItem =
  | {
      type: string
      mediaId: string
      /** [0, 1] */
      weight: number
    }
  | {
      type: 'inpaint'
      mediaId: string
      weight?: number
      maskMediaId: string
    }
  | {
      // reference only controlNet
      type: 'reference_only'
      mediaId: string
      weight: number
      thresholdA: number
    }

type TaskSubmitFormContextType = {
  loading: boolean
  setMaxPromptLength: (maxLength: number | null) => void
  submit: (
    extraValues?: MaybeAsyncGetter<TaskSubmitFormValues>,
    submitResponseCallback?: (res: CreateGenerationTaskMutation) => void,
    options?: {
      hideSubmissionToast?: boolean
      /** Handle errors from caller instead of submit fn itself */
      throwError?: boolean
      headers?: Record<string, string>
    },
  ) => any

  values: TaskSubmitFormValues
  handleChange: HandleChange<TaskSubmitFormValues>
  getValues: () => Promise<TaskSubmitFormValues> | undefined
  setValues: Dispatch<SetStateAction<TaskSubmitFormValues>>

  initialValues: TaskSubmitFormValues
  setInitialValues: (...args: any) => void
  reset: () => void

  validation: ValidateTaskParamsResult<TaskSubmitFormValues>
}

const TaskSubmitFormContext = createContext<TaskSubmitFormContextType>({
  loading: false,
  setMaxPromptLength: () => {},
  submit: () => {},
  values: {},
  /**
   * Only used for initialization, it lacks real handleChange functionality;
   * the actual handleChangeHandler is initialized at line 300 in this file, inside useForm hook.
   */
  handleChange: createHandleChange(),
  getValues: () => undefined,
  setValues: () => {},
  initialValues: {},
  setInitialValues: () => {},
  reset: () => {},
  validation: { ok: true } as ValidateTaskParamsResult<TaskSubmitFormValues>,
})
TaskSubmitFormContext.displayName = 'TaskSubmitFormContext'

export const useTaskSubmitFormContext = () => useContext(TaskSubmitFormContext)

type OnSuccessCallback = () => any

const BaseTaskSubmitForm: FC<{
  onSuccess?: OnSuccessCallback
  children?: ReactNode
}> = ({ onSuccess = () => {}, children }) => {
  const { t } = useTranslation(['generation'])
  const form = useForm<TaskSubmitFormValues>({})
  const { values, getValues, setValues } = form

  const [activityId, setActivityId] = useState(genId())

  useEffect(() => {
    setActivityId(genId())
  }, [values.baseImageTaskId])

  useEffect(() => {
    if (!values.prompts || values.prompts.trim().length <= 1) {
      setActivityId(genId())
    }
  }, [values])

  const maxPromptLengthRef = useRef<number | null>(null)

  const { data: userInfo } = useMyInfo()
  const { refetch: refetchQuota } = useMyQuota()
  const [promptHelperMode, setPromptHelperMode] = usePromptHelperMode()
  const [disableAutoPromptHelper] = useDisableAutoPromptHelperPref()

  const [loading, submit] = useLoadingCallback<
    TaskSubmitFormContextType['submit']
  >(
    async (_extraValues, submitResponseCallback = () => {}, options) => {
      const baseValues = await getValues()
      if (!baseValues) {
        console.log('got empty values in BaseTaskSubmitForm')
        return
      }

      const extraValues = await resolveGetter(_extraValues)

      const values: TaskSubmitFormValues = {
        ...baseValues,
        ...extraValues,
        extra: {
          ...baseValues?.extra,
          ...extraValues?.extra,
        },
      }

      const maxPromptLength = maxPromptLengthRef.current
      if (maxPromptLength != null) {
        const len = values.prompts?.length ?? 0
        if (len > maxPromptLength) {
          return toast.error(
            t('generation:prompts.notice.max-length-exceeded', {
              maxLength: maxPromptLength,
            }),
          )
        }
      }

      if (!isValuesCnetGenerativeFill(values)) {
        // TODO: refactor this for better readability and maintainability
        if (
          !values.prompts &&
          (values.loraParameters == null ||
            values.loraParameters?.every(
              info => (info?.triggerWords?.length ?? 0) === 0,
            ))
        ) {
          return toast.warn(
            t('generation:submit.notice.prompts-cannot-be-empty'),
          )
        }
      }

      // `AND` in prompts with ControlNets enabled will cause generation failure
      if (isValuesControlNet(values) && isValuesLatentCouple(values)) {
        return UIDialog.alert({
          heading: t('generation:submit.notice.latent-with-cn-warn.title'),
          content: t(
            'generation:submit.notice.latent-with-cn-warn.description',
          ),
          dismissable: false,
        })
      }

      try {
        const [baseImageMedia, maskImageMedia] =
          values.baseImage || values.inpaintMaskImage
            ? await toast.promise(
                Promise.all(
                  (
                    [
                      'baseImage',
                      'inpaintMaskImage',
                    ] satisfies (keyof TaskSubmitFormValues)[]
                  ).map(key =>
                    values[key]?.toMedia({
                      onCompressed(file) {
                        setValues(prev => ({ ...prev, [key]: file }))
                      },
                    }),
                  ),
                ),
                { pending: `${t('generation:submit.notice.uploading-image')}` },
              )
            : []

        const modelVersion = await getModelVersion(values.modelId)

        let naturalPrompts: string | undefined
        if (values.enablePromptsRewrite) {
          naturalPrompts = values.prompts
        }

        let parameters = getCreateTaskParameters(values, {
          baseImageMedia,
          maskImageMedia,
          naturalPrompts,
          modelVersion,
        })

        // Merge kaisuukenId if provided (benefit card / free generation)
        if (values.kaisuukenId) {
          parameters = { ...parameters, kaisuukenId: values.kaisuukenId }
        }

        if (!isChatModel(modelVersion?.modelType)) {
          parameters = mergePromptHelperParamsToParameter(parameters, {
            userWantToEnable: promptHelperMode,
          })
          if (parameters.promptHelper) {
            parameters.promptHelper.forcePromptHelperDetectionSide = 'server'
          }
        }

        if (
          checkLoraAndStyleKeyWarn({ values, taskModelVersion: modelVersion })
        ) {
          const toProceed = await openLoraStyleKeyWarnDialog()
          if (!toProceed) return
        }

        let taskResponse: CreateGenerationTaskMutation | null
        if (!options?.hideSubmissionToast) {
          const res = await toast.promise(
            executeCreateGenerationTask(
              { parameters },
              { context: { headers: options?.headers } },
            ),
            {
              pending: `${t('generation:submit.notice.task-submitting')}`,
              success: `${t('generation:submit.notice.task-submitted')}`,
            },
          )
          taskResponse = res.data ?? null
        } else {
          const res = await executeCreateGenerationTask(
            { parameters },
            { context: { headers: options?.headers } },
          )
          taskResponse = res.data ?? null
        }
        if (taskResponse == null) {
          throw new Error('Task creation failed')
        }

        // Handle server-side prompt helper detection result
        if (!isChatModel(modelVersion?.modelType)) {
          const detectResult =
            taskResponse.createGenerationTask?.detectPromptHelperResult
          // Fallback: if server didn't return detect result, default to enable
          const needToEnable = detectResult?.needToEnable ?? true
          if (needToEnable) {
            if (detectResult) {
              match(detectResult.enableReasonCode)
                .with('contains-non-english', 'prompts-too-short', reason => {
                  openPromptsHelperEnabledHintDialog(reason)
                })
                .otherwise(() => {})
            }
            values.enablePromptsRewrite = true
            setPromptHelperMode(true)
          }
        }

        track(
          'generate_task_submit',
          getCreateTaskTrackAttrs({
            taskId: taskResponse?.createGenerationTask?.id,
            activityId,
            ...values,
          }),
        )

        // tell user if their prompts contains sensitive words
        handlePromptsFilterResultInTaskResponse(taskResponse)
        // submit callback with response as arg
        submitResponseCallback(taskResponse)
        // callback from component props
        onSuccess()
        // update user quota after task submission
        refetchQuota()
      } catch (err: any) {
        // Skip error handling
        if (options?.throwError) {
          throw err
        }
        matchTaskSubmitFormError(t, err, {
          submit,
          taskParameters: values,
          onWatermarkNotAvailable: createWatermarkDisableHandler(
            values,
            setValues,
            submit,
          ),
        })
      } finally {
        updateEventGroup('generate_task_id', UpdateEventGroupAction.Increment)
      }
    },
    [
      userInfo,
      activityId,
      disableAutoPromptHelper,
      promptHelperMode,
      setValues,
    ],
  )

  const [initialValues, setInitialValues] = useState<TaskSubmitFormValues>({})
  const reset = useCallback(() => {
    setValues(initialValues)
  }, [initialValues, setValues])

  const validation = useValidateTaskParams(values)

  const throttledSubmit = useThrottledCallbackWithReturnValue(submit, 2000)

  const setMaxPromptLength = useCallback((maxLength: number | null) => {
    maxPromptLengthRef.current = maxLength
  }, [])

  const ctxValue = useMemo(() => {
    return {
      loading,
      setMaxPromptLength,
      submit: throttledSubmit,
      ...form,
      initialValues,
      setInitialValues,
      reset,
      validation,
    }
  }, [
    loading,
    setMaxPromptLength,
    throttledSubmit,
    form,
    initialValues,
    reset,
    validation,
  ])

  return (
    <TaskSubmitFormContext.Provider value={ctxValue}>
      {children}
    </TaskSubmitFormContext.Provider>
  )
}

export default BaseTaskSubmitForm

/**
 * Factory function to create a watermark not available error handler
 * Disables watermark and retries submission if watermark is currently enabled
 */
export function createWatermarkDisableHandler(
  values: TaskSubmitFormValues | undefined,
  setValues: (
    updater: (prev: TaskSubmitFormValues) => TaskSubmitFormValues,
  ) => void,
  submitFn: () => void,
): () => void {
  return () => {
    // Only retry if watermark is currently enabled (avoid redundant submissions)
    if (!values?.watermark) {
      return
    }
    // Update form state to disable watermark for UI consistency
    setValues(prev => ({
      ...prev,
      watermark: undefined,
    }))
    // Resubmit immediately without waiting for state update
    // Note: This works because the submit function will read the updated values
    // from the form state in its closure on the next render cycle
    submitFn()
  }
}

export function matchTaskSubmitFormError(
  t: TFunction<('common' | 'generation')[]>,
  err: any,
  opts?: {
    submit?: () => Promise<any>
    taskParameters?: Record<string, any>
    onWatermarkNotAvailable?: () => void
  },
) {
  const errCode = getErrorCode(err)
  const trackedErrCode = classifyErrorForTelemetry(err)
  track('generation_failed', {
    ...(opts?.taskParameters
      ? getCreateTaskTrackAttrs(opts.taskParameters)
      : undefined),
    errCode: trackedErrCode,
  })

  // Capture errors that we can't categorize into Sentry so we can investigate
  // them. Skip the well-known buckets: those are tracked in Amplitude and
  // would just be noise here.
  if (
    trackedErrCode === 'CLIENT_DERIVED_UNKNOWN' ||
    trackedErrCode === 'GRAPHQL_UNKNOWN'
  ) {
    withScope(scope => {
      scope.setTag('errCode', trackedErrCode)
      scope.setContext('generation_failed', {
        modelId: opts?.taskParameters?.modelId,
        taskCategory: opts?.taskParameters?.taskCategory,
        priority: opts?.taskParameters?.priority,
      })
      captureException(
        err instanceof Error
          ? err
          : new Error(`generation_failed unknown error: ${String(err)}`),
      )
    })
  }
  return match(errCode)
    .with('INSUFFICIENT_BALANCE', () => {
      openMembershipMoreCreditsModal({
        trackingLocation: 'insufficient-credit-task-gen',
        scene: 'insufficient-credit-task-gen',
        reason: t(
          'generation:submit.user-insufficient-credit-dialog.description',
        ),
        defaultMode: 'packs',
      })
    })
    .with('WAITING_NUM_EXCEEDED', async () => {
      await UIDialog.alert({
        heading: t('generation:submit.waiting-num-exceeded-dialog.title'),
        content: t('generation:submit.waiting-num-exceeded-dialog.desc'),
      })
    })
    .with('WATERMARK_NOT_AVAILABLE', async () => {
      // If handler is available, show confirm dialog; otherwise show alert-only
      if (opts?.onWatermarkNotAvailable) {
        const confirmed = await UIDialog.confirm({
          heading: t('generation:submit.watermark-not-available-dialog.title'),
          content: t('generation:submit.watermark-not-available-dialog.desc'),
          confirmProps: {
            children: t(
              'generation:submit.watermark-not-available-dialog.confirm',
            ),
          },
          cancelProps: {
            children: t(
              'generation:submit.watermark-not-available-dialog.cancel',
            ),
          },
        })
        if (confirmed) {
          opts.onWatermarkNotAvailable()
        }
      } else {
        // Handler not available; show alert with explanation
        await UIDialog.alert({
          heading: t('generation:submit.watermark-not-available-dialog.title'),
          content: t('generation:submit.watermark-not-available-dialog.desc'),
        })
      }
    })
    .with('NSFW_PROMPTS_DETECTED', () => {
      const promptFiltersAction =
        extractPromptsModerationActionFromNsfwError(err)

      // Reduce sensitive feedback rate to 25%
      const showSensitiveFeedback = Math.random() > 0.75

      match([promptFiltersAction, showSensitiveFeedback])
        .with(
          [
            P.union(
              PromptsModerationActions.REJECT_FEEDBACK,
              PromptsModerationActions.WARN_FEEDBACK,
            ),
            true,
          ],
          ([val]) => {
            return openSensitiveFeedbackToast(val, {
              modelId: opts?.taskParameters?.modelId,
              prompts: opts?.taskParameters?.prompts,
            })
          },
        )
        .with(
          [PromptsModerationActions.REJECT_SET_NSFW_GUIDANCE, P.any],
          () => {
            toast.warn(t('generation:submit.notice.set-nsfw-guidance'))
          },
        )
        .otherwise(() => {
          toast.warn(t('generation:submit.notice.nsfw-prompts-detected'))
        })
    })
    .with('NSFW_PROMPTS_DETECTED_ADULT_NOT_SET', async () => {
      track('age_restriction_dialog_show')
      const restrictionLifted = await checkAgeRestriction()
      if (restrictionLifted) return opts?.submit?.()
    })
    .with('NO_PERMISSION_FOR_PREVIEW', () => {
      openModelPreviewTestIneligibleDialog()
    })
    .with('NO_PERMISSION_FOR_EARLY_ACCESS', () => {
      openModelEarlyAccessNeedMembershipDialog()
    })
    .with('KAISUUKEN_NOT_AVAILABLE', () => {
      toast.warn(t('generation:submit.notice.kaisuuken-not-available'))
    })
    .with('KAISUUKEN_NOT_ALLOWED', () => {
      toast.warn(t('generation:submit.notice.kaisuuken-not-allowed'))
    })
    .otherwise(() => {
      toast.error(`${t('common:error.fail-to-submit')} ${err?.message}`)
    })
}

function handlePromptsFilterResultInTaskResponse(
  res: CreateGenerationTaskMutation,
) {
  if (!res.createGenerationTask) return
  const { moderationAction: { promptsModerationAction } = {} } =
    res.createGenerationTask
  if (typeof promptsModerationAction === 'string') {
    // Bypass PASS action
    if (promptsModerationAction !== PromptsModerationActions.PASS)
      openSensitiveFeedbackToast(
        promptsModerationAction as PromptsModerationAction,
        {
          taskId: res.createGenerationTask.id,
          modelId: res.createGenerationTask.parameters.modelId,
          prompts: res.createGenerationTask.parameters.prompts,
        },
      )
  }
  return
}
